/**
 * ExternalSection Component
 *
 * Displays external examination data in a tabular format showing OD (right eye) and OS (left eye)
 * findings for different anatomical systems including lids/lashes, face, cornea, lacrimal, orbit,
 * bulbar conjunctiva, lens, iris, sclera, lymph nodes, palpebral conjunctiva, and anterior chamber.
 * Provides an editable interface for updating external examination findings.
 *
 * Follows Apple's design guidelines with consistent glass morphism UI and accessibility features.
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { CheckCircle, Edit3, Loader2, Save, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface ExternalData {
  // Lids/Lashes
  externLidsLashesOD?: string;
  externLidsLashesOS?: string;

  // Face
  externFaceOD?: string;
  externFaceOS?: string;

  // Cornea
  externCorneaOD?: string;
  externCorneaOS?: string;

  // Lacrimal
  externLacrimalOD?: string;
  externLacrimalOS?: string;

  // Orbit
  externOrbitOD?: string;
  externOrbitOS?: string;

  // Bulbar Conjunctiva
  externBulbConjOD?: string;
  externBulbConjOS?: string;

  // Lens
  externLensOD?: string;
  externLensOS?: string;

  // Iris
  externIrisOD?: string;
  externIrisOS?: string;

  // Sclera
  externScleraOD?: string;
  externScleraOS?: string;

  // Lymph Nodes
  externLymphNodesOD?: string;
  externLymphNodesOS?: string;

  // Palpebral Conjunctiva
  externPalpConjOD?: string;
  externPalpConjOS?: string;

  // Anterior Chamber
  externAcOD?: string;
  externAcOS?: string;

  [key: string]: any; // Allow for other properties in the JSON
}

interface ExternalApiResponse {
  testKey: string;
  data: ExternalData[];
}

// Legacy data structure interface (for backward compatibility)
interface ExternalLegacyData {
  externLidsLashesOD?: string;
  externLidsLashesOS?: string;
  externFaceOD?: string;
  externFaceOS?: string;
  externCorneaOD?: string;
  externCorneaOS?: string;
  externLacrimalOD?: string;
  externLacrimalOS?: string;
  externOrbitOD?: string;
  externOrbitOS?: string;
  externBulbConjOD?: string;
  externBulbConjOS?: string;
  externLensOD?: string;
  externLensOS?: string;
  externIrisOD?: string;
  externIrisOS?: string;
  externScleraOD?: string;
  externScleraOS?: string;
  externLymphNodesOD?: string;
  externLymphNodesOS?: string;
  externPalpConjOD?: string;
  externPalpConjOS?: string;
  externAcOD?: string;
  externAcOS?: string;
  [key: string]: any; // Allow for other properties in the JSON
}

// Nested structure interface (for pages[0].data format)
interface ExternalNestedData {
  pages?: Array<{
    data?: ExternalData;
    [key: string]: any;
  }>;
  [key: string]: any;
}

// Union type to handle both new and legacy formats, including array format
type ExternalDataUnion =
  | ExternalApiResponse
  | ExternalLegacyData
  | ExternalApiResponse[]
  | ExternalNestedData
  | null;

interface ExternalSectionProps {
  jsonContent: ExternalDataUnion;
  isLoading?: boolean;
  error?: string | null;
  index?: number;
  onUpdate: (updatedData: ExternalDataUnion) => Promise<void>;
  onAccept?: () => void;
}

const ExternalSection = ({
  jsonContent,
  isLoading = false,
  error = null,
  index = 0,
  onUpdate,
  onAccept,
}: ExternalSectionProps) => {
  const [currentData, setCurrentData] = useState<ExternalData | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editValues, setEditValues] = useState<ExternalData>({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Helper function to extract the actual external data from either format
  const extractExternalData = useCallback(
    (data: ExternalDataUnion): ExternalData | null => {
      if (!data) return null;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(data) && data.length > 0) {
        const firstItem = data[0];
        if (
          'testKey' in firstItem &&
          'data' in firstItem &&
          Array.isArray(firstItem.data)
        ) {
          return firstItem.data[0] || null;
        }
      }

      // Check if it's the new API response format (single object)
      if ('testKey' in data && 'data' in data && Array.isArray(data.data)) {
        return data.data[0] || null;
      }

      // Check if it's the nested structure with pages[0].data
      if ('pages' in data && data.pages?.[0]?.data) {
        return data.pages[0].data;
      }

      // Legacy format: direct object with external fields
      if (
        'externLidsLashesOD' in data ||
        'externFaceOD' in data ||
        'externCorneaOD' in data
      ) {
        return data as ExternalData;
      }

      return null;
    },
    []
  );

  // Helper function to create the appropriate data structure for updates
  const createUpdateData = useCallback(
    (updatedFields: ExternalData): ExternalDataUnion => {
      if (!jsonContent) return null;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(jsonContent) && jsonContent.length > 0) {
        const firstItem = jsonContent[0];
        if (
          'testKey' in firstItem &&
          'data' in firstItem &&
          Array.isArray(firstItem.data)
        ) {
          const updatedDataArray = [
            {
              ...firstItem.data[0],
              ...updatedFields,
            },
          ];

          return [
            {
              ...firstItem,
              data: updatedDataArray,
            },
          ];
        }
      }

      // Check if it's the new API response format (single object)
      if (
        'testKey' in jsonContent &&
        'data' in jsonContent &&
        Array.isArray(jsonContent.data)
      ) {
        const updatedDataArray = [
          {
            ...jsonContent.data[0],
            ...updatedFields,
          },
        ];

        return {
          ...jsonContent,
          data: updatedDataArray,
        };
      }

      // Check if it's the nested structure with pages[0].data
      if ('pages' in jsonContent && jsonContent.pages?.[0]?.data) {
        return {
          ...jsonContent,
          pages: [
            {
              ...jsonContent.pages[0],
              data: {
                ...jsonContent.pages[0].data,
                ...updatedFields,
              },
            },
          ],
        };
      }

      // Legacy format: return direct object
      return {
        ...jsonContent,
        ...updatedFields,
      };
    },
    [jsonContent]
  );

  // Update local state when jsonContent changes
  useEffect(() => {
    const extractedData = extractExternalData(jsonContent);
    setCurrentData(extractedData);
  }, [jsonContent, extractExternalData]);

  // Handle updates to the external data
  const handleUpdate = useCallback(
    async (updatedFields: ExternalData) => {
      if (!currentData) return;

      const updatedData = createUpdateData(updatedFields);

      // Update local state optimistically
      setCurrentData({
        ...currentData,
        ...updatedFields,
      });

      try {
        await onUpdate(updatedData);
      } catch (error) {
        // Revert local state on error
        const extractedData = extractExternalData(jsonContent);
        setCurrentData(extractedData);
        throw error;
      }
    },
    [currentData, jsonContent, onUpdate, createUpdateData, extractExternalData]
  );

  const handleEditClick = () => {
    // Initialize edit values with current data
    setEditValues(currentData || {});
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    setIsUpdating(true);
    try {
      await handleUpdate(editValues);
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update external examination:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditDialogOpen(false);
    setEditValues({});
  };

  const handleInputChange = (field: string, value: string) => {
    setEditValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Define anatomical systems with their field mappings
  const anatomicalSystems = [
    {
      name: 'Lids/Lashes',
      odField: 'externLidsLashesOD',
      osField: 'externLidsLashesOS',
    },
    {
      name: 'Face',
      odField: 'externFaceOD',
      osField: 'externFaceOS',
    },
    {
      name: 'Cornea',
      odField: 'externCorneaOD',
      osField: 'externCorneaOS',
    },
    {
      name: 'Lacrimal',
      odField: 'externLacrimalOD',
      osField: 'externLacrimalOS',
    },
    {
      name: 'Orbit',
      odField: 'externOrbitOD',
      osField: 'externOrbitOS',
    },
    {
      name: 'Bulbar Conj.',
      odField: 'externBulbConjOD',
      osField: 'externBulbConjOS',
    },
    {
      name: 'Lens',
      odField: 'externLensOD',
      osField: 'externLensOS',
    },
    {
      name: 'Iris',
      odField: 'externIrisOD',
      osField: 'externIrisOS',
    },
    {
      name: 'Sclera',
      odField: 'externScleraOD',
      osField: 'externScleraOS',
    },
    {
      name: 'Lymph Nodes',
      odField: 'externLymphNodesOD',
      osField: 'externLymphNodesOS',
    },
    {
      name: 'Palpebral Conj.',
      odField: 'externPalpConjOD',
      osField: 'externPalpConjOS',
    },
    {
      name: 'Anterior Chamber',
      odField: 'externAcOD',
      osField: 'externAcOS',
    },
  ];

  // Helper function to get display value with fallback
  const getDisplayValue = (value: string | undefined) => value || '—';

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.4, ease: 'easeOut' }}
        className="group">
        {/* Main card */}
        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-lg border border-white/30 dark:border-white/15 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all duration-300">
          {/* Inner highlight */}
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-white/20 dark:to-white/8 pointer-events-none" />

          {/* Content */}
          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-slate-800 dark:text-slate-200 font-semibold text-xl tracking-wide">
                  External
                </h3>
                {isLoading && (
                  <div className="flex items-center gap-1 ml-2">
                    <Loader2 className="w-3 h-3 animate-spin text-eyevantage-primary" />
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      Loading...
                    </span>
                  </div>
                )}
              </div>
              {currentData && !error && (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick();
                    }}
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 bg-slate-100/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-gray-400/40 dark:border-white/20 text-slate-700 dark:text-slate-300 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                    disabled={isLoading}>
                    <Edit3 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  {onAccept && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAccept();
                      }}
                      size="sm"
                      className="h-8 px-3 bg-emerald-100/50 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-emerald-600/60 dark:border-white/20 text-emerald-700 dark:text-emerald-700 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                      disabled={isLoading}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Accept
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="space-y-4">
              {error ? (
                <div className="text-slate-500 dark:text-slate-400 text-md italic">
                  {error === 'Data not found' ? (
                    'No external examination data available yet'
                  ) : (
                    <div className="text-red-600 dark:text-red-400">
                      <span className="font-medium">Error:</span> {error}
                    </div>
                  )}
                </div>
              ) : isLoading ? (
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-md">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Fetching external examination data...</span>
                </div>
              ) : currentData ? (
                /* External Examination Table */
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th
                          style={{ width: '35%' }}
                          className="border border-gray-400/40 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-md font-semibold text-slate-700 dark:text-slate-300 text-center">
                          OD
                        </th>
                        <th
                          style={{ width: '30%' }}
                          className="border border-gray-400/40 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-md font-semibold text-slate-700 dark:text-slate-300 text-center">
                          System
                        </th>

                        <th
                          style={{ width: '35%' }}
                          className="border border-gray-400/40 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-md font-semibold text-slate-700 dark:text-slate-300 text-center">
                          OS
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {anatomicalSystems.map((system, idx) => (
                        <tr
                          key={system.name}
                          className={
                            idx % 2 === 0
                              ? 'bg-slate-50/30 dark:bg-slate-800/30'
                              : ''
                          }>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-md text-slate-700 dark:text-slate-300 text-center">
                            {getDisplayValue(currentData[system.odField])}
                          </td>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-md font-medium text-slate-700 dark:text-slate-300 text-center">
                            <strong> {system.name}</strong>
                          </td>
                          <td className="border border-gray-400/40 dark:border-slate-700 px-3 py-2 text-md text-slate-700 dark:text-slate-300 text-center">
                            {getDisplayValue(currentData[system.osField])}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-slate-500 dark:text-slate-400 text-md italic">
                  No external examination data available yet
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent
          className="max-w-5xl max-h-[80vh] overflow-y-auto bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg border border-white/30 dark:border-white/15"
          onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="text-slate-800 dark:text-slate-200 font-semibold text-xl">
              Edit External
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Table Header */}
            <div className="grid grid-cols-3 gap-2 p-2 border-b border-gray-400/40 dark:border-slate-700">
              <div className="text-md font-semibold text-slate-700 dark:text-slate-300">
                OD (Right Eye)
              </div>
              <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                System
              </div>
              <div className="text-md font-semibold text-slate-700 dark:text-slate-300">
                OS (Left Eye)
              </div>
            </div>

            {/* Anatomical Systems in Table Format */}
            <div className="space-y-3">
              {anatomicalSystems.map((system, idx) => (
                <div
                  key={system.name}
                  className={`grid grid-cols-3 gap-2 p-0 rounded-lg`}>
                  {/* OD Input */}
                  <div className="space-y-1">
                    <Input
                      id={system.odField}
                      value={editValues[system.odField] || ''}
                      onChange={(e) =>
                        handleInputChange(system.odField, e.target.value)
                      }
                      placeholder={`e.g., Clear, Normal, etc.`}
                      className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15"
                    />
                  </div>

                  {/* System Name */}
                  <div className="flex items-center justify-center">
                    <Label className="text-lg font-bold text-slate-700 dark:text-slate-300 text-center">
                      {system.name}
                    </Label>
                  </div>

                  {/* OS Input */}
                  <div className="space-y-1">
                    <Input
                      id={system.osField}
                      value={editValues[system.osField] || ''}
                      onChange={(e) =>
                        handleInputChange(system.osField, e.target.value)
                      }
                      placeholder={`e.g., Clear, Normal, etc.`}
                      className="h-8 text-md bg-white/70 dark:bg-slate-500/70 border border-gray-400/40 dark:border-white/15"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-white/80 dark:bg-slate-800/80 border border-gray-400/40 dark:border-white/20">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleSaveEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-500 hover:to-blue-800 text-white">
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExternalSection;
